import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { DisclaimerModal } from '../components/assessment/DisclaimerModal';
import { SignInModal } from '../components/auth/SignInModal';
import { ShieldCheck, ArrowRight, FileText, Activity, Lock } from 'lucide-react';
import { useAssessment } from '../hooks/useAssessment';
import { useAuth } from '../hooks/useAuth';
export function LandingPage() {
  const navigate = useNavigate();
  const {
    resetAssessment
  } = useAssessment();
  const {
    signIn,
    isAuthenticated
  } = useAuth();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const handleStartClick = () => {
    setShowDisclaimer(true);
  };
  const handleAcceptDisclaimer = () => {
    resetAssessment();
    setShowDisclaimer(false);
    navigate('/assessment/system');
  };
  const handleSignInClick = () => {
    setShowSignIn(true);
  };
  const handleSignInSuccess = async (email: string, password: string) => {
    const success = await signIn(email, password);
    if (success) {
      setShowSignIn(false);
      navigate('/library');
    }
    return success;
  };
  return <div className="min-h-screen bg-navy-900 flex flex-col" data-id="element-810">
      <nav className="border-b border-slate-800 bg-navy-900/50 backdrop-blur-sm" data-id="element-811">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between" data-id="element-812">
          <div className="flex items-center space-x-3" data-id="element-813">
            <ShieldCheck className="h-8 w-8 text-teal-500" data-id="element-814" />
            <span className="text-xl font-bold text-white tracking-tight" data-id="element-815">
              ProvenanceOne
            </span>
          </div>
          <div className="flex space-x-4" data-id="element-816">
            <Button variant="ghost" data-id="element-817">Documentation</Button>
            {isAuthenticated ? <Button variant="ghost" onClick={() => navigate('/library')} data-id="element-818">
                Library
              </Button> : <Button variant="ghost" onClick={handleSignInClick} data-id="element-819">
                Sign In
              </Button>}
          </div>
        </div>
      </nav>

      <main className="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8 py-20 text-center" data-id="element-820">
        <div className="max-w-3xl mx-auto space-y-8" data-id="element-821">
          <div className="inline-flex items-center space-x-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 mb-4" data-id="element-822">
            <span className="flex h-2 w-2 rounded-full bg-teal-500 animate-pulse" data-id="element-823"></span>
            <span className="text-sm font-medium text-teal-400" data-id="element-824">
              EU AI Act Compliance Ready
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-white tracking-tight leading-tight" data-id="element-825">
            Traceable, Governable AI <br data-id="element-826" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-600" data-id="element-827">
              Compliance Infrastructure
            </span>
          </h1>

          <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-828">
            The control layer for high-stakes AI. Ground your systems in
            regulation, classify risk with precision, and build a defensible
            evidence trail.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4" data-id="element-829">
            <Button size="lg" onClick={handleStartClick} className="w-full sm:w-auto group" data-id="element-830">
              Start New Assessment
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-831" />
            </Button>
            <Button size="lg" variant="secondary" className="w-full sm:w-auto" data-id="element-832">
              View Demo Audit
            </Button>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto w-full text-left" data-id="element-833">
          <div className="bg-navy-800 p-8 rounded-xl border border-slate-800" data-id="element-834">
            <div className="h-12 w-12 bg-slate-700/50 rounded-lg flex items-center justify-center mb-6" data-id="element-835">
              <FileText className="h-6 w-6 text-teal-400" data-id="element-836" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-3" data-id="element-837">
              Regulatory Grounding
            </h3>
            <p className="text-slate-400 leading-relaxed" data-id="element-838">
              Start with the law, not a checklist. Map your system directly to
              EU AI Act obligations.
            </p>
          </div>

          <div className="bg-navy-800 p-8 rounded-xl border border-slate-800" data-id="element-839">
            <div className="h-12 w-12 bg-slate-700/50 rounded-lg flex items-center justify-center mb-6" data-id="element-840">
              <Activity className="h-6 w-6 text-teal-400" data-id="element-841" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-3" data-id="element-842">
              Defensible Risk Scoring
            </h3>
            <p className="text-slate-400 leading-relaxed" data-id="element-843">
              Classify systems with precision using our forensic risk wizard.
              Remove ambiguity.
            </p>
          </div>

          <div className="bg-navy-800 p-8 rounded-xl border border-slate-800" data-id="element-844">
            <div className="h-12 w-12 bg-slate-700/50 rounded-lg flex items-center justify-center mb-6" data-id="element-845">
              <Lock className="h-6 w-6 text-teal-400" data-id="element-846" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-3" data-id="element-847">
              Evidence Chain
            </h3>
            <p className="text-slate-400 leading-relaxed" data-id="element-848">
              Create an immutable audit trail of policies, data lineage, and
              human oversight.
            </p>
          </div>
        </div>
      </main>

      {/* Modals */}
      <DisclaimerModal isOpen={showDisclaimer} onClose={() => setShowDisclaimer(false)} onAccept={handleAcceptDisclaimer} data-id="element-849" />

      <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} onSignIn={handleSignInSuccess} data-id="element-850" />
    </div>;
}